import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Span,
  useToast,
} from "@ir/ui";
import {
  CreateMasterDataProductUomDTO,
  DeleteMasterDataProductUomsDTO,
  UpdateMasterDataProductUomDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataProductUom } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataProductUomUseCase,
  DeleteMasterDataProductUomsUseCase,
  UpdateMasterDataProductUomUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataProductUomRepository } from "@src/infrastructure/repositories/masterData";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useUserPermissions } from "@src/presentation/hooks/permissions";
import { useMasterDataProductUoms } from "@src/presentation/providers/MasterDataProductUomsDataProvider";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataProductUomAddEditForm,
  type ProductUomAddEditFormValues,
} from "../utils/MasterDataProductUomAddEditForm";
import { MasterDataProductUomsTable } from "../utils/MasterDataProductUomsTable";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import { getMasterDataSaveErrorMessage } from "../utils/masterDataSaveErrors";
import {
  PRODUCT_UOM_CODE_ALREADY_EXISTS_MESSAGE,
  isProductUomCodeDuplicateError,
} from "../utils/productUomCodeValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_FORM_VALUES: ProductUomAddEditFormValues = {
  uomName: "",
  uomCode: "",
  uomDescription: "",
};

const LINKED_DELETE_WARNING = "Cannot delete UOM linked to a product.";

export const MasterDataProductUomsPage = () => {
  const { productUoms, count, loading, error, search, setSearch, refetch } =
    useMasterDataProductUoms();
  const { success: successToast, error: errorToast } = useToast();
  const { hasAnyPermission } = useUserPermissions();
  const [form] = Form.useForm<ProductUomAddEditFormValues>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = hasAnyPermission([Permissions.MASTER_DATA_DELETE]);

  const {
    modalMode,
    editingEntity: editingUom,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataProductUom>();

  const repository = useMemo(() => new MasterDataProductUomRepository(), []);
  const createUseCase = useMemo(
    () => new CreateMasterDataProductUomUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataProductUomUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteMasterDataProductUomsUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): ProductUomAddEditFormValues => {
    if (modalMode === "edit" && editingUom) {
      return {
        uomName: editingUom.uomName,
        uomCode: editingUom.uomCode,
        uomDescription: editingUom.uomDescription,
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingUom, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: ProductUomAddEditFormValues) => {
      setIsSaving(true);
      try {
        const uomName = values.uomName.trim();
        const uomCode = values.uomCode.trim();
        const uomDescription = values.uomDescription.trim();

        if (modalMode === "edit" && editingUom) {
          await updateUseCase.execute(
            new UpdateMasterDataProductUomDTO({
              uomId: editingUom.uomId,
              uomName,
              uomCode: editingUom.uomCode,
              uomDescription,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataProductUomDTO({
              uomName,
              uomCode,
              uomDescription,
            }),
          );
        }

        successToast({ content: "Product UOM saved successfully" });
        closeModal();
        refetch();
      } catch (err) {
        if (isProductUomCodeDuplicateError(err)) {
          form.setFields([
            {
              name: "uomCode",
              errors: [PRODUCT_UOM_CODE_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save product UOM. Please try again.",
              "Failed to save product UOM. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingUom,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    const uomIds = Array.from(selectedIds);
    if (uomIds.length === 0) {
      setIsDeleteConfirmOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteUseCase.execute(
        new DeleteMasterDataProductUomsDTO({ uomIds }),
      );

      if (result.deletedCount > 0) {
        successToast({
          content: `Deleted ${result.deletedCount} product UOM${
            result.deletedCount === 1 ? "" : "s"
          }.`,
        });
      }
      if (result.blockedCount > 0) {
        errorToast({ content: LINKED_DELETE_WARNING });
      }

      setSelectedIds(new Set());
      setIsDeleteConfirmOpen(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : "Failed to delete product UOMs. Please try again.";
      errorToast({ content: message });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteUseCase, errorToast, refetch, selectedIds, successToast]);

  const selectedCount = selectedIds.size;

  return (
    <>
      <MasterDataWritableListLayout
        title="Product UOM"
        subtitle={
          error ?? formatMasterDataRecordCount(loading, count)
        }
        searchPlaceholder="Search product UOMs"
        search={search}
        setSearch={setSearch}
        searchSection="product-uom"
        addButtonLabel="Add Product UOM"
        onAdd={openCreateModal}
        headerActions={
          <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_DELETE]}>
            {selectedCount > 0 ? (
              <Button
                size="small"
                color="error"
                variant="outline"
                onPress={() => setIsDeleteConfirmOpen(true)}
                className="ms-2"
              >
                <Icon slot="start" name={Icons.Trash} />
                Delete Selected
              </Button>
            ) : null}
          </HasAnyPermission>
        }
        hideSearch={selectedCount > 0}
        loading={loading}
      >
        <MasterDataProductUomsTable
          productUoms={productUoms}
          search={search}
          setSearch={setSearch}
          onEdit={openEditModal}
          showSelection={canDelete}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Product UOM"
        editTitle="Edit Product UOM"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataProductUomAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeleting) setIsDeleteConfirmOpen(false);
        }}
        size="small"
        closable={!isDeleting}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Delete selected UOMs
          </Heading>
        }
        footerContent={
          <Flex gap={0.5} className="flex-1" justifyContent="space-between">
            <Button
              size="small"
              color="grey"
              isDisabled={isDeleting}
              onPress={() => setIsDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              isLoading={isDeleting}
              isDisabled={isDeleting}
              onPress={() => {
                void handleConfirmDelete();
              }}
            >
              Delete Selected
            </Button>
          </Flex>
        }
        classNames={{
          body: "p-0",
        }}
      >
        <Paragraph className="m-0 py-4 px-3">
          Are you sure you want to delete{" "}
          <Span className="font-bold text-primary-500">{selectedCount}</Span>
          <Span className="ps-1">
            {selectedCount === 1 ? "UOM" : "UOMs"}
          </Span>
          ?
        </Paragraph>
      </Modal>
    </>
  );
};
